// src/utils/copyToClipboard.js
import toast from "react-hot-toast";

/**
 * Copy text to clipboard with toast notification
 * Uses modern Clipboard API (writeText) with fallback for older browsers
 * @param {string} text - The text to copy
 * @param {Object} options - Optional configuration
 * @param {string} options.successMessage - Custom success message
 * @param {string} options.errorMessage - Custom error message
 * @returns {Promise<boolean>} - Returns true if copy was successful
 */
const copyToClipboard = async (text, { successMessage, errorMessage } = {}) => {
  if (!text) {
    toast.error(errorMessage || "Nothing to copy");
    return false;
  }

  const textToCopy = String(text);

  // Use modern Clipboard API if available (requires HTTPS)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(successMessage || "Copied to clipboard");
      return true;
    } catch (err) {
      // Clipboard API failed (might be due to permissions or context)
      // Fall through to fallback method
    }
  }

  // Fallback for older browsers or when Clipboard API is unavailable
  try {
    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    // execCommand is deprecated but needed as fallback for older browsers
    // Using type assertion to bypass TypeScript deprecation warning
    /** @type {any} */
    const doc = document;
    const success = doc.execCommand("copy");
    document.body.removeChild(textArea);

    if (success) {
      toast.success(successMessage || "Copied to clipboard");
      return true;
    } else {
      throw new Error("execCommand('copy') returned false");
    }
  } catch (fallbackErr) {
    toast.error(errorMessage || "Failed to copy to clipboard");
    return false;
  }
};

export default copyToClipboard;
